import AccountTreeIcon from "@mui/icons-material/AccountTree";
import useSessionPermissionsLoadState from "permissions/session/use-session-permissions-load-state";
import AddCommentIcon from "@mui/icons-material/AddComment";
import ArchiveIcon from "@mui/icons-material/Archive";
import AssignmentIcon from "@mui/icons-material/Assignment";
import CloseIcon from "@mui/icons-material/Close";
import FilterListIcon from "@mui/icons-material/FilterList";
import GroupIcon from "@mui/icons-material/Group";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import GroupsIcon from "@mui/icons-material/Groups";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
import MarkEmailUnreadIcon from "@mui/icons-material/MarkEmailUnread";
import MessageIcon from "@mui/icons-material/Message";
import PageviewIcon from "@mui/icons-material/Pageview";
import PersonIcon from "@mui/icons-material/Person";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import SearchIcon from "@mui/icons-material/Search";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import UnarchiveIcon from "@mui/icons-material/Unarchive";
import WorkIcon from "@mui/icons-material/Work";
import Autocomplete from "@mui/material/Autocomplete";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { styled, SxProps, Theme } from "@mui/material/styles";
import Tab from "@mui/material/Tab";
import type { TableRowProps } from "@mui/material/TableRow";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import PaginatedResponse from "_shared/_api/pagination/responses/paginated-response";
import type { ApiErrorResult } from "_shared/_errors/api-error";
import { NotAuthorized } from "_shared/_errors/_views/not-authorized";
import { useDialog } from "_shared/_providers/dialogs/dialog";
import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import EmptyInboxPlaceholder from "_shared/_views/assets/empty-inbox-all-caught-up.svg";
import Guid from "_shared/guids/guid";
import * as Config from "config";
import {
  type MRT_ColumnFiltersState as ColumnFiltersState,
  MRT_GlobalFilterTextField as GlobalFilterTextField,
  createMRTColumnHelper,
  MRT_TableOptions,
  type MRT_PaginationState as PaginationState,
  type MRT_RowData as RowData,
  MRT_ShowHideColumnsButton as ShowHideColumnsButton,
  type MRT_TableInstance as TableInstance,
  type MRT_Column as TableColumn,
  MRT_ToggleFiltersButton as ToggleFiltersButton,
  MRT_ToggleFullScreenButton as ToggleFullScreenButton,
  type MRT_Updater,
} from "material-react-table";
import {
  ToolbarButtons,
  searchFieldSpacing,
} from "_shared/_views/tables/table-toolbar";
import type { MarketplaceSearchInfoJSON } from "marketplace/_schema/marketplace-search-response-schema";
import { useSearchIndividualsQuery } from "marketplace/_api/marketplace-api";
import ChatDialog from "messaging/forums/_views/components/forum-chat-dialog";
import ConversationTable from "messaging/forums/_views/components/forums-table";
import { NotificationTag } from "notifications/_views/notification-tag";
import {
  type CommunicationTableSearchState,
  type SortingState,
  applyUpdater,
  getColumnFiltersFromSearchParams,
  getGlobalFilterFromSearchParams,
  getNotificationQueryFromSearchParams,
  getPaginationFromSearchParams,
  getSearchParamsFromTableState,
  getSortingFromSearchParams,
  shouldPopulateNotificationQuery,
} from "notifications/_views/communications-page-table-state";
import {
  useArchiveNotificationMutation,
  useArchiveNotificationsMutation,
  useMarkNotificationAsSeenMutation,
  useMarkNotificationAsUnseenMutation,
  useMarkNotificationsAsSeenMutation,
  useMarkNotificationsAsUnseenMutation,
  useUnarchiveNotificationMutation,
} from "notifications/_api/notifications-api";
import Notification from "notifications/notification";
import {
  handleNotificationMutationFailure,
  type NotificationOperation,
} from "notifications/notification-error";
import {
  isNotificationNavigable,
  NotificationLink,
  NotificationNavigationState,
  NotificationType,
} from "notifications/notification-type";
import {
  addNotification,
  addNotifications,
  getErrorLoadingNotificationsByQuery,
  getIsLoadingNotificationsByQuery,
  getLastSuccessfulNotificationResponseByInterface,
  getNotificationQueryByInterface,
  getNotificationsByQuery,
  isNotificationLoadIssue,
  NotificationLoadIssue,
  populateNotificationsByQuery,
  updateNotificationInterfaceQuery,
} from "notifications/_stores/notifications-redux-slice";
import useNotificationProposals, {
  type NotificationProposal,
  type NotificationProposalDetails,
} from "notifications/_views/_hooks/use-notification-proposals";
import NotificationContent from "notifications/_views/notification-content";
import { Action } from "notifications/notification-action";
import { BoxType } from "notifications/notification-box-type";
import TopicCategory from "notifications/topics/topic-category";
import React, { JSX, useCallback, useEffect, useMemo, useState } from "react";
import { enqueueSnackbar } from "notistack";
import { useSelector } from "react-redux";
import { useLoaderData, useLocation, useNavigate, useSearchParams } from "react-router";
import { useSession } from "users/__session/session-context";
import Individual from "marketplace/individuals/individual";
import { useGetIndividualsByUserIdsQuery } from "marketplace/individuals/_api/individual-api";
import { getSessionPermissions } from "permissions/_stores/permissions-redux-slice";
import { skipToken } from "@reduxjs/toolkit/query";
import { MRTNoMemo } from "_shared/_views/tables/table";
import { emptyRowsFallback } from "_shared/_views/tables/table-empty-state";
import RowActions from "_shared/_views/tables/row-actions";
import TruncatedCell from "_shared/_views/tables/truncated-cell";
import InputAdornment from "@mui/material/InputAdornment";

const ToolbarButtonsContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
  },
  display: "flex",
  alignItems: "end",
  flexDirection: "column",
}));
const ToolbarActionButtons = styled("div")(() => ({
  textAlign: "right",
  whiteSpace: "nowrap",
}));
const CategoryItem = styled(ListItem)(({ theme }) => ({
  margin: 0,
  padding: 0,
  minWidth: 0,
  "& .MuiListItemText-root": {
    minWidth: 0,
  },
  "& .MuiListItemText-primary, & .MuiListItemText-secondary": {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  "& .MuiListItemText-secondary": {
    fontSize: "0.75rem",
  },
  [theme.breakpoints.down("md")]: {
    "& .MuiListItemIcon-root": {
      display: "none",
    },
  },
}));
const AlertActions = styled("div")(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  gap: theme.spacing(1),
  flexWrap: "nowrap",
  height: "100%",
  paddingRight: theme.spacing(2),
  whiteSpace: "nowrap",
}));
const AllCaughtUp = styled("img")(({ theme }) => ({
  maxHeight: "100px",
  padding: theme.spacing(2),
}));
const TableTopToolbarContainer = styled("div")(() => ({
  display: "flex",
  flex: "1 1 0",
  flexDirection: "column",
  minWidth: 0,
  maxWidth: "100%",
}));
const LowerTopToolbarContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    alignItems: "stretch",
    flexDirection: "column",
  },
  gap: theme.spacing(1),
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  minWidth: 0,
  width: "100%",
}));
const CommunicationsTabs = styled(Tabs)(() => ({
  flexShrink: 0,
  maxWidth: "100%",
  minWidth: 0,
  "& .MuiTabs-scroller": {
    minWidth: 0,
  },
}));
const MobileToolbarButtonsContainer = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  width: "100%",
  marginTop: theme.spacing(1),
}));
const TableTitle = styled(Typography)(({ theme }) => ({
  paddingLeft: theme.spacing(1),
}));
const FilterButtons = styled("div")(({ theme }) => ({
  display: "flex",
  flexShrink: 0,
  flexWrap: "wrap",
  gap: theme.spacing(0.5),
  marginTop: 0,
  minWidth: 0,
  width: "auto",
  [theme.breakpoints.down("md")]: {
    display: "grid",
    gridAutoColumns: "auto",
    gridAutoFlow: "column",
    marginTop: theme.spacing(1),
    width: "100%",
  },
  "& > .MuiChip-root": {
    [theme.breakpoints.down("md")]: {
      justifyContent: "center",
      marginBottom: 0,
      maxWidth: "none",
      width: "100%",
    },
  },
}));
const TableTitleContainer = styled("div")(() => ({
  alignItems: "center",
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
  width: "100%",
}));

const SenderCell = ({
  senderUserId,
  senderName,
  sender,
  isLoading,
}: {
  senderUserId?: Guid;
  senderName?: string;
  sender?: Individual;
  isLoading: boolean;
}) => {
  if (senderName) return <TruncatedCell>{senderName}</TruncatedCell>;
  if (!senderUserId) return <TruncatedCell>System</TruncatedCell>;
  if (isLoading && !sender) return <TruncatedCell>Loading...</TruncatedCell>;
  if (!sender) return <Icon>remove</Icon>;

  return <TruncatedCell>{sender.fullName}</TruncatedCell>;
};

const RecipientsCell = ({
  recipientUserIds,
  recipientName,
  recipients,
  isLoading,
}: {
  recipientUserIds?: Guid[];
  recipientName?: string;
  recipients: Individual[];
  isLoading?: boolean;
}) => {
  if (recipientName) return <TruncatedCell>{recipientName}</TruncatedCell>;
  if (!recipientUserIds || recipientUserIds.length === 0) {
    return <Icon>remove</Icon>;
  }

  if (isLoading) return <TruncatedCell>Loading...</TruncatedCell>;
  if (!recipients.length) return <Icon>remove</Icon>;

  return (
    <TruncatedCell>
      {recipients.map((recipient, index) => {
        if (!recipient) return null;
        return (
          <React.Fragment key={recipient?.id.value}>
            {recipient.fullName}
            {index < recipients.length - 1 && ", "}
          </React.Fragment>
        );
      })}
    </TruncatedCell>
  );
};

type IndividualFilterOption = {
  label: string;
  secondaryLabel?: string;
  value: string;
};

const INDIVIDUAL_FILTER_SEARCH_DEBOUNCE_MS = 250;

/** The notification ids of the rows the reader has ticked. */
export function selectedNotificationIds(rowSelection: Record<string, boolean>): string[] {
  return Object.keys(rowSelection).filter((id) => rowSelection[id]);
}

/**
 * Applies one bulk mark-seen or mark-unseen to the ticked rows.
 *
 * Every notification the API returns is folded into a **single** `addNotifications` dispatch. Doing
 * it one at a time cleared the query cache once per row, so ticking a screenful and marking it read
 * re-rendered the table once per notification (GitHub issue #2963).
 *
 * It lives at module scope rather than inside the component so the two callers share one
 * implementation, and so the React Compiler leaves its `try`/`catch` control flow alone.
 */
export async function applyBulkSeenChange({
  notificationIds,
  changeNotifications,
  dispatch,
  clearSelection,
  handleError,
}: Readonly<{
  notificationIds: string[];
  changeNotifications: (notificationIds: string[]) => Promise<Notification[] | undefined>;
  dispatch: (action: ReturnType<typeof addNotifications>) => void;
  clearSelection: () => void;
  handleError: (error: unknown) => unknown | Promise<unknown>;
}>): Promise<void> {
  try {
    const updatedNotifications = await changeNotifications(notificationIds);
    if (updatedNotifications) {
      dispatch(addNotifications(updatedNotifications));
    }
    clearSelection();
  } catch (error) {
    await handleError(error);
  }
}

/**
 * Restores selected notifications one at a time because the API only offers a single-item restore.
 * A deleted notification is already absent from the desired projection, so keep restoring the
 * remaining rows and refresh once at the end. Other failures stop the batch to avoid an ambiguous
 * partial write.
 */
export async function applyBulkUnarchiveChange({
  notificationIds,
  unarchiveNotification,
  refresh,
  clearSelection,
  handleError,
}: Readonly<{
  notificationIds: string[];
  unarchiveNotification: (notificationId: string) => Promise<void>;
  refresh: () => Promise<void>;
  clearSelection: () => void;
  handleError: (error: unknown) => Promise<ApiErrorResult>;
}>): Promise<void> {
  for (const notificationId of notificationIds) {
    try {
      await unarchiveNotification(notificationId);
    } catch (error) {
      const result = await handleError(error);
      if (result.status === 404) continue;
      return;
    }
  }

  await refresh();
  clearSelection();
}

/**
 * Icon for a notification's topic category — and, for proposals, its `actionRequired`.
 *
 * The proposal branch is total by construction: an action this bundle has no specific icon for
 * still gets an icon rather than an empty slot beside a perfectly readable notification.
 */
export function notificationCategoryIcon(topicCategory: TopicCategory, actionRequired: string | undefined) {
  if (topicCategory === TopicCategory.Messaging.Forum || topicCategory === TopicCategory.Messaging.ForumInvitation) {
    return <MessageIcon />;
  }
  if (
    topicCategory === TopicCategory.Users.NetworkConnection ||
    topicCategory === TopicCategory.Users.NetworkInvitation
  ) {
    return <AccountTreeIcon />;
  }
  if (topicCategory === TopicCategory.Work.Proposal) {
    if (!actionRequired) return <PersonAddIcon />;
    if (actionRequired === Action.Review || actionRequired === Action.ProposalReReviewRequested) {
      return <PageviewIcon />;
    }
    if (
      actionRequired === Action.ProposalReviewerAccessChanged ||
      actionRequired === Action.DraftProposalReviewerAccessChanged
    ) {
      return <ManageAccountsIcon />;
    }
    if (actionRequired === Action.ProposalReviewerRemoved) {
      return <PersonRemoveIcon />;
    }
    return <AssignmentIcon />;
  }
  if (topicCategory === TopicCategory.Work.Agreement) return <WorkIcon />;
  if (topicCategory === TopicCategory.Marketplace.Team) return <GroupIcon />;
  if (topicCategory === TopicCategory.Marketplace.TeamInvitation) return <GroupAddIcon />;
  if (topicCategory === TopicCategory.LegalEntities.TeamRoster) return <GroupsIcon />;
  if (topicCategory === TopicCategory.LegalEntities.EntityMember) return <PersonIcon />;
  if (topicCategory === TopicCategory.LegalEntities.LegalEntity) return <SupervisorAccountIcon />;
  return null;
}

/**
 * The Category column's cell: the topic icon, the type label in bold until the reader has seen the
 * row, and under it which proposal the row is about and the stage that proposal is at.
 *
 * Exported so it can be covered directly rather than through a rendered page — see the note at the
 * top of `communications-page.spec.tsx`.
 */
export function notificationCategoryCell(
  notification: NotificationType | NotificationLoadIssue,
  options: Readonly<{
    userId: Guid | undefined;
    proposals?: NotificationProposalDetails;
  }>,
): JSX.Element {
  if (isNotificationLoadIssue(notification)) {
    return (
      <CategoryItem dense>
        <ListItemIcon>
          <Icon color="error">error</Icon>
        </ListItemIcon>
        <ListItemText primary={<strong style={{ fontWeight: "normal" }}>Unknown</strong>} />
      </CategoryItem>
    );
  }

  const topicCategory: TopicCategory = notification?.topic?.category ?? "";

  return (
    <CategoryItem dense>
      <ListItemIcon>{notificationCategoryIcon(topicCategory, notification.actionRequired)}</ListItemIcon>
      <ListItemText
        primary={
          <TruncatedCell component="span">
            <strong style={{ fontWeight: Notification.isSeenByUser(notification, options.userId) ? "normal" : "bold" }}>
              {getNotificationTypeLabel(notification)}
            </strong>
          </TruncatedCell>
        }
        secondary={notificationCategoryDetail(notification, options)}
      />
    </CategoryItem>
  );
}

/**
 * The lines under the type label: which proposal the row is about and the stage that proposal is
 * at.
 *
 * The type label above these says "Work Proposal" for every proposal row, so without the name two
 * rows about two different proposals are told apart only by opening them (#5248). The name goes
 * first because it is what the reader is looking for; the stage under it is the volatile fact and
 * keeps the lighter weight of the two.
 *
 * Most proposal notifications also name the proposal somewhere in their own message — "{name}
 * proposal reviewed by …" — but that prose is truncated to the Description column's width and the
 * name sits at a different place in each wording, at the end of the ones that read "… requested
 * your review on {name}". A row cannot rely on it, so the name is repeated here, where it has a
 * fixed place and the same clipped-text hover as everything else in this column. A column of its
 * own would have cost the row width for the same fact.
 *
 * The stage lives here rather than beside the Description's tags because that column is narrow and
 * already carries the row's own message; a stage as long as "Awaiting Team Leader Approval" pushed
 * the message out of it entirely (#5129). Here it sits in the column that already says what the
 * row is about, with room to spell itself out.
 *
 * Nothing says whether a row has been read. The row's background and the weight of its type label
 * already say an unread row is unread, the way the bell's rows do (#5147) — see the note on
 * `NotificationTitle` in `notification-bell` — and a "Read at …" line under every row the reader
 * has opened spent the column's width restating that (#5248). The exact moment is on the row
 * already, in the Date column.
 */
function notificationCategoryDetail(
  notification: NotificationType,
  options: Readonly<{ proposals?: NotificationProposalDetails }>,
): JSX.Element {
  const proposal = getNotificationProposal(notification, options.proposals);
  const proposalStatusLabel = proposal?.statusLabel ?? getProposalStatusPlaceholder(notification, options.proposals);

  return (
    <>
      {proposal?.name && (
        <TruncatedCell component="span">
          <span style={{ fontWeight: 500 }}>{proposal.name}</span>
        </TruncatedCell>
      )}
      {proposalStatusLabel && <TruncatedCell component="span">{proposalStatusLabel}</TruncatedCell>}
    </>
  );
}

/**
 * The Description column's cell: the notification's own content, preceded by the tags that say what
 * the row stands for — Received/Sent on the Archive tab, and how many messages an aggregated row
 * folded together (#830).
 *
 * Exported so it can be covered directly rather than through a rendered page — see the note at the
 * top of `communications-page.spec.tsx`.
 */
export function notificationDescriptionCell(
  notification: NotificationType | NotificationLoadIssue,
  options: Readonly<{ activeTab: BoxType; userId: Guid | undefined }>,
): JSX.Element {
  if (isNotificationLoadIssue(notification)) {
    return <Typography color="error">There was an issue loading this notification&apos;s details.</Typography>;
  }
  const tag = Notification.userIsRecipient(notification, options.userId) ? (
    <NotificationTag size="small" label="Received" />
  ) : (
    <NotificationTag size="small" label="Sent" />
  );
  // The content below is the most recent message of the run this row stands for (#830); the tag
  // is what says there were earlier ones.
  const occurrenceLabel = getNotificationOccurrenceLabel(notification);
  // A structured notification stacks its subheader, body, and fine print as separate lines, each
  // clipped on its own, and the cell's own text runs them together with nothing between them. So the
  // hover is handed the parts with their breaks restored. A notification without them renders one
  // line of markdown, and the text of that line as the reader sees it is already the whole of it.
  const structuredContent = [notification.subheader, notification.body, notification.finePrint]
    .filter(Boolean)
    .join("\n");
  return (
    <TruncatedCell title={structuredContent}>
      {options.activeTab === BoxType.Archive && tag}
      {occurrenceLabel && <NotificationTag size="small" label={occurrenceLabel} />}
      <NotificationContent notification={notification} />
    </TruncatedCell>
  );
}

/**
 * The proposal this row is about, once it has arrived.
 *
 * Nothing at all for a row about something else, and nothing once the request has finished without
 * the proposal in it — which is the answer for a proposal the reader may not open.
 */
function getNotificationProposal(
  notification: NotificationType,
  proposals: NotificationProposalDetails | undefined,
): NotificationProposal | undefined {
  const topicId = getNotificationProposalTopicId(notification);
  if (!topicId) return undefined;

  return proposals?.byId.get(topicId);
}

/**
 * `Loading...` where the stage goes while the proposals are still on their way.
 *
 * They come back in their own request, seconds after the rows on a cold start, so a proposal row
 * would otherwise sit with a blank where its stage goes and read as a proposal that has none.
 * `Loading...` is what the From and To columns say in the same situation. The name says nothing in
 * the meantime: two placeholders stacked would say the same thing twice.
 */
function getProposalStatusPlaceholder(
  notification: NotificationType,
  proposals: NotificationProposalDetails | undefined,
): string | undefined {
  if (!proposals?.isLoading) return undefined;

  return getNotificationProposalTopicId(notification) ? "Loading..." : undefined;
}

/**
 * The id of the proposal a row is about, or nothing for a row about anything else.
 *
 * It is the topic's *category* that says a row is about a proposal, not its id: a row about
 * something else must never borrow a proposal from an id that happens to match.
 */
function getNotificationProposalTopicId(notification: NotificationType): string | undefined {
  if (notification.topic?.category !== TopicCategory.Work.Proposal) return undefined;

  return notification.topic?.id?.value;
}

function notificationRowStyle(
  state: Readonly<{ backgroundColor: string; isArchived: boolean; onSelect?: () => void }>,
): TableRowProps {
  const sx: SxProps<Theme> = {
    backgroundColor: state.backgroundColor,
    cursor: state.onSelect && !state.isArchived ? "pointer" : "default",
  };

  return {
    hover: Boolean(state.onSelect),
    onClick: state.onSelect,
    sx,
  };
}

/**
 * How one row of the communications table looks, and whether clicking it goes anywhere.
 *
 * A notification whose category has no route gets no click handler, no hover highlight, and no
 * pointer cursor, so the row never offers navigation it cannot perform — network connection
 * announcements are the case this was added for (#4849). The row's own controls (mark read,
 * archive) are unaffected; they are buttons inside the row with their own handlers.
 */
export function notificationRowProps(
  notification: NotificationType | NotificationLoadIssue,
  options: Readonly<{ isArchived: boolean; userId: Guid | undefined; onSelect: () => void }>,
): TableRowProps {
  if (isNotificationLoadIssue(notification)) {
    // A row that failed to load keeps the click it has always had, even though it goes nowhere.
    return notificationRowStyle({
      backgroundColor: options.isArchived ? "rgba(0, 0, 0, 0.04)" : "rgba(255, 100, 100, 0.1)",
      isArchived: options.isArchived,
      onSelect: options.onSelect,
    });
  }

  const isSeen = Notification.isSeenByUser(notification, options.userId);
  return notificationRowStyle({
    backgroundColor: options.isArchived || isSeen ? "rgba(0, 0, 0, 0.04)" : "inherit",
    isArchived: options.isArchived,
    onSelect: isNotificationNavigable(notification) ? options.onSelect : undefined,
  });
}

function CommunicationIndividualFilter({
  column,
  individuals,
  label,
}: Readonly<{
  column: TableColumn<NotificationType | NotificationLoadIssue, string>;
  individuals: Individual[];
  label: string;
}>) {
  const filterValue = column.getFilterValue() as string | undefined;
  const [inputValue, setInputValue] = useState("");
  const [debouncedInputValue, setDebouncedInputValue] = useState("");

  useEffect(() => {
    const trimmedInput = inputValue.trim();
    const timeout = globalThis.setTimeout(() => {
      setDebouncedInputValue(trimmedInput);
    }, INDIVIDUAL_FILTER_SEARCH_DEBOUNCE_MS);

    return () => globalThis.clearTimeout(timeout);
  }, [inputValue]);

  const individualSearchQuery = useSearchIndividualsQuery(
    debouncedInputValue
      ? {
          search: {
            from: 0,
            query: debouncedInputValue,
            size: 10,
          },
        }
      : skipToken,
  );

  const searchOptions = useMemo(
    () => getIndividualFilterOptionsFromSearchResults(individualSearchQuery.data?.hits?.map((hit) => hit.data) ?? []),
    [individualSearchQuery.data],
  );

  const selectedOption = useMemo(() => {
    if (!filterValue) return null;

    const selectedIndividual = individuals.find((individual) => individual.userId.value === filterValue);
    if (selectedIndividual) return getIndividualFilterOptionFromIndividual(selectedIndividual);

    return searchOptions.find((option) => option.value === filterValue) ?? { label: filterValue, value: filterValue };
  }, [filterValue, individuals, searchOptions]);

  const options = useMemo(
    () => dedupeIndividualFilterOptions([...(selectedOption ? [selectedOption] : []), ...searchOptions]),
    [searchOptions, selectedOption],
  );
  const displayedInputValue = inputValue || selectedOption?.label || "";

  return (
    <Autocomplete<IndividualFilterOption, false, false, false>
      filterOptions={(options) => options}
      getOptionLabel={(option) => option.label}
      inputValue={displayedInputValue}
      isOptionEqualToValue={(option, value) => option.value === value.value}
      loading={individualSearchQuery.isFetching}
      loadingText="Searching..."
      noOptionsText={inputValue.trim() ? "No individuals found" : "Type a name"}
      onChange={(_event, option) => {
        column.setFilterValue(option?.value || undefined);
        setInputValue("");
      }}
      onInputChange={(_event, newInputValue, reason) => {
        if (reason === "reset") return;

        if (
          reason === "clear" ||
          !newInputValue ||
          (filterValue && newInputValue !== selectedOption?.label)
        ) {
          column.setFilterValue(undefined);
        }

        setInputValue(newInputValue);
      }}
      options={options}
      renderOption={(props, option) => (
        <li {...props}>
          <ListItemText primary={option.label} secondary={option.secondaryLabel} />
        </li>
      )}
      renderInput={(params) => (
        <TextField {...params} label={label} placeholder="Search by name" size="small" variant="standard" />
      )}
      value={selectedOption}
    />
  );
}

const createCommunicationIndividualFilter = (individuals: Individual[], label: string) =>
  function CommunicationIndividualFilterRenderer({
    column,
  }: Readonly<{ column: TableColumn<NotificationType | NotificationLoadIssue, string> }>) {
    return <CommunicationIndividualFilter column={column} individuals={individuals} label={label} />;
  };

const columnHelper = createMRTColumnHelper<NotificationType | NotificationLoadIssue>();

export default function CommunicationsPage() {
  const [rowSelection, setRowSelection] = React.useState<Record<string, boolean>>({});

  const routeParams = useLoaderData();
  const [searchParams, setSearchParams] = useSearchParams();
  const routePageTab = isBoxType(routeParams.pageTab) ? routeParams.pageTab : undefined;
  const isComposeRoute = routeParams.action === "compose" || routeParams.action === "create";
  const activeTab = isComposeRoute ? getStoredCommunicationsTab() ?? BoxType.Inbox : routePageTab ?? BoxType.Inbox;
  const filterByUnread = searchParams.has("unread") ? searchParams.get("unread") === "true" : undefined;
  const filterByActionRequired = searchParams.has("actionRequired")
    ? searchParams.get("actionRequired") === "true"
    : undefined;
  const globalFilter = useMemo(() => getGlobalFilterFromSearchParams(searchParams), [searchParams]);
  const columnFilters = useMemo(() => getColumnFiltersFromSearchParams(searchParams), [searchParams]);
  const sorting = useMemo(() => getSortingFromSearchParams(searchParams), [searchParams]);
  const pagination = useMemo(() => getPaginationFromSearchParams(searchParams), [searchParams]);
  const navigate = useNavigate();
  const location = useLocation();
  const session = useSession();
  const sessionPermissions = useSelector(getSessionPermissions);
  const permissionsLoadState = useSessionPermissionsLoadState();
  const dispatch = useAttorneyHubDispatch();
  const { openDialog } = useDialog();
  const isMedDisplaySize = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));
  const isXlDisplaySize = useMediaQuery((theme: Theme) => theme.breakpoints.down("xl"));
  const actionsColumnSize = activeTab === BoxType.Inbox && !isMedDisplaySize ? 88 : 48;

  const interfaceName = "communicationsPage";

  const notificationQuery = useSelector(getNotificationQueryByInterface(interfaceName));
  const defaultQuery = useMemo(() => {
    return getNotificationQueryFromSearchParams(searchParams, activeTab);
  }, [activeTab, searchParams]);

  const effectiveQuery = notificationQuery ?? defaultQuery;
  const queryResponse = useSelector(getNotificationsByQuery(effectiveQuery));
  const queryIsLoading = useSelector(getIsLoadingNotificationsByQuery(effectiveQuery));
  const errorLoadingQuery = useSelector(getErrorLoadingNotificationsByQuery(effectiveQuery));
  const lastSuccessfulResponse = useSelector(getLastSuccessfulNotificationResponseByInterface(interfaceName));
  const tableNotifications = queryResponse ?? lastSuccessfulResponse;
  const selectedFilterUserIds = useMemo(() => getSelectedNotificationFilterUserIds(columnFilters), [columnFilters]);

  const userIdsToLoad = useMemo(() => {
    const userIds = getValidNotificationUserIds(tableNotifications);
    selectedFilterUserIds.forEach((userId) => userIds.add(userId));
    return userIds;
  }, [selectedFilterUserIds, tableNotifications]);

  const individualQuery = useGetIndividualsByUserIdsQuery(userIdsToLoad.size ? { userIds: userIdsToLoad } : skipToken);
  const proposals = useNotificationProposals(tableNotifications);
  const [markNotificationAsSeen] = useMarkNotificationAsSeenMutation();
  const [markNotificationsAsSeen] = useMarkNotificationsAsSeenMutation();
  const [markNotificationAsUnseen] = useMarkNotificationAsUnseenMutation();
  const [markNotificationsAsUnseen] = useMarkNotificationsAsUnseenMutation();
  const [archiveNotification] = useArchiveNotificationMutation();
  const [archiveNotifications] = useArchiveNotificationsMutation();
  const [unarchiveNotification] = useUnarchiveNotificationMutation();

  const refreshNotifications = useCallback(async () => {
    await dispatch(populateNotificationsByQuery({
      session,
      query: effectiveQuery,
      interfaceName,
    }));
  }, [dispatch, effectiveQuery, interfaceName, session]);

  const handleMutationError = useCallback(
    (error: unknown, operation: NotificationOperation, refreshNotFound = true) => handleNotificationMutationFailure({
      error,
      operation,
      refresh: refreshNotifications,
      notify: (message) => enqueueSnackbar(message, { variant: "error" }),
      refreshNotFound,
    }),
    [refreshNotifications],
  );

  const individuals = useMemo(
    () => Individual.fromJSONArray(individualQuery.data, { allowEmpty: true }) ?? [],
    [individualQuery.data],
  );

  const openComposeRoute = useCallback(() => {
    navigate(`/communications/${activeTab}/compose`);
  }, [activeTab, navigate]);

  const openMessageDialog = useCallback(
    (id?: Guid) => {
      const closeTab = isComposeRoute ? activeTab : routePageTab ?? activeTab;
      const closePath = `/communications/${closeTab}`;

      openDialog({
        title: id ? "Message" : "New Message",
        resetStack: true,
        MuiProps: {
          fullWidth: true,
          maxWidth: "md",
        },
        contentSxProps: {
          display: "flex",
          overflowX: "hidden",
        },
        hideFullScreenButton: true,
        component: <ChatDialog forumId={id} />,
        onClose: () => void navigate(closePath),
      });
    },
    [activeTab, isComposeRoute, navigate, openDialog, routePageTab],
  );

  useEffect(() => {
    if (isComposeRoute) return;
    storeCommunicationsTab(activeTab);
  }, [activeTab, isComposeRoute]);


  useEffect(() => {
    if (!notificationQuery) return;
    if (!shouldPopulateNotificationQuery({ notificationQuery, queryIsLoading, errorLoadingQuery, queryResponse })) {
      return;
    }

    dispatch(
      populateNotificationsByQuery({
        session,
        query: notificationQuery,
        interfaceName,
      }),
    );
  }, [dispatch, session, interfaceName, notificationQuery, queryIsLoading, queryResponse, errorLoadingQuery]);

  // Handle URL changes
  useEffect(() => {
    if (!session.user?.legalEntityId) return;

    if (!routeParams.pageTab) {
      navigate(`/communications/${BoxType.Inbox}`);
      return;
    }
    if (routeParams.action === "create") {
      openMessageDialog();
      return;
    }
    if (routeParams.action === "compose") {
      openMessageDialog();
      return;
    }
    if (routeParams.pageTab === BoxType.Forums) {
      if (routeParams.id) {
        openMessageDialog(new Guid(routeParams.id));
      } else if (routeParams.action) {
        openMessageDialog(new Guid(routeParams.action));
      }
    }
  }, [
    routeParams.pageTab,
    routeParams.action,
    routeParams.id,
    session.user?.legalEntityId,
    navigate,
    openMessageDialog,
  ]);

  useEffect(() => {
    if (!session.user?.legalEntityId) return;

    const newQuery = getNotificationQueryFromSearchParams(searchParams, activeTab);
    if (newQuery.isEqualTo(notificationQuery)) {
      return;
    }

    dispatch(
      updateNotificationInterfaceQuery({
        interfaceName,
        query: newQuery,
      }),
    );
  }, [
    session.user?.legalEntityId,
    searchParams,
    activeTab,
    notificationQuery,
    dispatch,
    interfaceName,
  ]);

  const updateSearchParamsFromTableState = useCallback(
    (nextState: CommunicationTableSearchState) => {
      const newSearchParams = getSearchParamsFromTableState(nextState, searchParams);
      if (newSearchParams.toString() === searchParams.toString()) return;
      setSearchParams(newSearchParams);
    },
    [searchParams, setSearchParams],
  );

  const setGlobalFilter = useCallback(
    (updater: MRT_Updater<string>) => {
      updateSearchParamsFromTableState({
        globalFilter: applyUpdater(updater, globalFilter),
        columnFilters,
        sorting,
        pagination,
      });
    },
    [columnFilters, globalFilter, pagination, sorting, updateSearchParamsFromTableState],
  );

  const setColumnFilters = useCallback(
    (updater: MRT_Updater<ColumnFiltersState>) => {
      updateSearchParamsFromTableState({
        globalFilter,
        columnFilters: applyUpdater(updater, columnFilters),
        sorting,
        pagination,
      });
    },
    [columnFilters, globalFilter, pagination, sorting, updateSearchParamsFromTableState],
  );

  const setSorting = useCallback(
    (updater: MRT_Updater<SortingState>) => {
      updateSearchParamsFromTableState({
        globalFilter,
        columnFilters,
        sorting: applyUpdater(updater, sorting),
        pagination,
      });
    },
    [columnFilters, globalFilter, pagination, sorting, updateSearchParamsFromTableState],
  );

  const setPagination = useCallback(
    (updater: MRT_Updater<PaginationState>) => {
      updateSearchParamsFromTableState({
        globalFilter,
        columnFilters,
        sorting,
        pagination: applyUpdater(updater, pagination),
      });
    },
    [columnFilters, globalFilter, pagination, sorting, updateSearchParamsFromTableState],
  );

  async function navigateToTarget(notification: NotificationType | NotificationLoadIssue) {
    if (isNotificationLoadIssue(notification)) {
      return;
    }
    try {
      if (activeTab === BoxType.Inbox) {
        await markNotificationAsSeen({ notificationId: notification.id.value }).unwrap();
      }
      const redirectUrl = NotificationLink(notification);
      // Categories with no route never reach here from a row click, since those rows carry no click
      // handler at all (#4849). Guard anyway rather than navigate to an empty path.
      if (!redirectUrl) return;
      const navigationState = NotificationNavigationState(notification, `${location.pathname}${location.search}`);
      if (navigationState) {
        navigate(redirectUrl, { state: navigationState });
      } else {
        navigate(redirectUrl);
      }
    } catch (error) {
      await handleMutationError(error, "mark-seen");
    }
  }

  const markMultipleSeen = useCallback(
    () =>
      applyBulkSeenChange({
        notificationIds: selectedNotificationIds(rowSelection),
        changeNotifications: (notificationIds) => markNotificationsAsSeen({ notificationIds }).unwrap(),
        dispatch,
        clearSelection: () => setRowSelection({}),
        handleError: (error) => handleMutationError(error, "mark-seen"),
      }),
    [dispatch, handleMutationError, markNotificationsAsSeen, rowSelection],
  );

  const markMultipleUnseen = useCallback(
    () =>
      applyBulkSeenChange({
        notificationIds: selectedNotificationIds(rowSelection),
        changeNotifications: (notificationIds) => markNotificationsAsUnseen({ notificationIds }).unwrap(),
        dispatch,
        clearSelection: () => setRowSelection({}),
        handleError: (error) => handleMutationError(error, "mark-unseen"),
      }),
    [dispatch, handleMutationError, markNotificationsAsUnseen, rowSelection],
  );

  const archiveMultiple = useCallback(async () => {
    const selectedRowIds: Guid[] = Object.keys(rowSelection)
      .filter((id) => rowSelection[id])
      .map((id) => new Guid(id));
    if (selectedRowIds.length === 0) return;

    try {
      await archiveNotifications({ notificationIds: selectedRowIds.map((id) => id.value) }).unwrap();
      await dispatch(
        populateNotificationsByQuery({
          session,
          query: effectiveQuery,
          interfaceName,
        }),
      );
      setRowSelection({});
    } catch (error) {
      await handleMutationError(error, "archive");
    }
  }, [archiveNotifications, dispatch, effectiveQuery, handleMutationError, interfaceName, rowSelection, session]);

  const unArchiveMultiple = useCallback(async () => {
    const selectedRowIds: Guid[] = Object.keys(rowSelection)
      .filter((id) => rowSelection[id])
      .map((id) => new Guid(id));

    try {
      await applyBulkUnarchiveChange({
        notificationIds: selectedRowIds.map((id) => id.value),
        unarchiveNotification: (notificationId) => unarchiveNotification({ notificationId }).unwrap(),
        refresh: refreshNotifications,
        clearSelection: () => setRowSelection({}),
        handleError: (error) => handleMutationError(error, "unarchive", false),
      });
    } catch (error) {
      await handleMutationError(error, "unarchive");
    }
  }, [handleMutationError, refreshNotifications, rowSelection, unarchiveNotification]);

  const renderCreatedDateColumn = useCallback(
    (notification: NotificationType | NotificationLoadIssue): React.ReactNode => {
      if (isNotificationLoadIssue(notification)) {
        return null;
      }
      if (!notification.createdDate) return null;
      return <TruncatedCell component="time">{notification.createdDate.format("MM/DD/YY hh:mm A")}</TruncatedCell>;
    },
    [],
  );

  const renderCategoryColumn = useCallback(
    (notification: NotificationType | NotificationLoadIssue) =>
      notificationCategoryCell(notification, { userId: session.user?.id, proposals }),
    [proposals, session.user?.id],
  );

  const renderDescriptionColumn = useCallback(
    (notification: NotificationType | NotificationLoadIssue) =>
      notificationDescriptionCell(notification, { activeTab, userId: session.user?.id }),
    [activeTab, session.user?.id],
  );

  const renderFromColumn = useCallback(
    (notification: NotificationType | NotificationLoadIssue) => {
      if (isNotificationLoadIssue(notification)) return null;

      const senderName =
        "messageInfo" in notification ? notification?.messageInfo?.senderName?.value : notification?.senderName?.value;
      const sender = individuals?.find((individual) => {
        return individual.userId.isEqualTo(notification.senderUserId);
      });
      const isLoading = individualQuery.isLoading;

      return (
        <SenderCell
          senderUserId={notification.senderUserId}
          senderName={senderName}
          sender={sender}
          isLoading={isLoading}
        />
      );
    },
    [individuals, individualQuery.isLoading],
  );

  const renderToColumn = useCallback(
    (notification: NotificationType | NotificationLoadIssue) => {
      if (isNotificationLoadIssue(notification)) return null;

      const recipients = notification.recipients?.map((r) => r.userId);
      if (!recipients?.length) return <Icon>remove</Icon>;
      const recipientIndividuals = individuals.filter((individual) =>
        recipients.some((recipientUserId) => individual.userId.isEqualTo(recipientUserId)),
      );

      return (
        <RecipientsCell
          recipientUserIds={recipients}
          recipientName={notification.recipientName?.value}
          recipients={recipientIndividuals}
          isLoading={individualQuery.isLoading}
        />
      );
    },
    [individuals, individualQuery.isLoading],
  );
  const renderInboxRowActions = (notification: NotificationType | NotificationLoadIssue) => {
    if (!(notification instanceof Notification)) return null;
    const isSeen = notification.isSeenByUser(session.user?.id);
    return (
      <RowActions
        ariaLabel={`Actions for ${getNotificationTypeLabel(notification)}`}
        actions={[
          {
            key: "seen",
            label: isSeen ? "Mark as Unread" : "Mark as Read",
            icon: <MarkEmailReadIcon />,
            onClick: () => void (isSeen ? markAsUnread(notification) : markAsRead(notification)),
          },
          {
            key: "archive",
            label: "Archive",
            icon: <ArchiveIcon />,
            onClick: () => void archive(notification),
          },
        ]}
      />
    );
  };

  const renderArchiveRowActions = (communication: NotificationType | NotificationLoadIssue) => {
    if (isNotificationLoadIssue(communication)) {
      return null;
    }
    return (
      <RowActions
        ariaLabel={`Actions for ${getNotificationTypeLabel(communication)}`}
        actions={[
          {
            key: "unarchive",
            label: "Restore from Archive",
            icon: <UnarchiveIcon />,
            onClick: () => void unArchive(communication),
          },
        ]}
      />
    );
  };

  async function markAsRead(communication: NotificationType) {
    if (!communication || communication?.seenByUserAt(session.user?.id)) return;
    try {
      const updatedNotification = await markNotificationAsSeen({ notificationId: communication.id.value }).unwrap();
      if (!updatedNotification) throw new Error("Notification not returned from API");
      dispatch(addNotification(updatedNotification));
    } catch (error) {
      await handleMutationError(error, "mark-seen");
    }
  }

  async function markAsUnread(communication: NotificationType) {
    if (!communication?.seenByUserAt(session.user?.id)) return;
    try {
      const updatedNotification = await markNotificationAsUnseen({ notificationId: communication.id.value }).unwrap();
      if (!updatedNotification) throw new Error("Notification not returned from API");
      dispatch(addNotification(updatedNotification));
    } catch (error) {
      await handleMutationError(error, "mark-unseen");
    }
  }

  async function archive(communication: NotificationType) {
    if (!communication) return;
    try {
      await archiveNotification({ notificationId: communication.id.value }).unwrap();
      await refreshNotifications();
    } catch (error) {
      await handleMutationError(error, "archive");
    }
  }

  async function unArchive(communication: NotificationType) {
    if (!communication) return;
    try {
      await unarchiveNotification({ notificationId: communication.id.value }).unwrap();
      await refreshNotifications();
    } catch (error) {
      await handleMutationError(error, "unarchive");
    }
  }

  const columns = useMemo(() => {
    const cols = [];

    if (activeTab !== BoxType.Forums) {
      cols.push(
        columnHelper.accessor(getNotificationTypeLabel, {
          header: "Type",
          id: "type",
          enableSorting: false,
          enableColumnFilter: true,
          Cell: ({ row }) => renderCategoryColumn(row.original),
        }),
        columnHelper.accessor(getNotificationSenderFilterValue, {
          header: "From",
          id: "from",
          enableSorting: true,
          enableColumnFilter: true,
          Filter: createCommunicationIndividualFilter(individuals, "From"),
          Cell: ({ row }) => renderFromColumn(row.original),
        }),
        columnHelper.accessor(getNotificationRecipientFilterValue, {
          header: "To",
          id: "to",
          enableSorting: true,
          enableColumnFilter: true,
          Filter: createCommunicationIndividualFilter(individuals, "To"),
          Cell: ({ row }) => renderToColumn(row.original),
        }),
        columnHelper.accessor(getNotificationDescriptionFilterValue, {
          header: "Description",
          id: "description",
          enableSorting: false,
          enableColumnFilter: true,
          Cell: ({ row }) => renderDescriptionColumn(row.original),
        }),
      );
    }

    cols.push(
      columnHelper.accessor(getNotificationCreatedDateSortValue, {
        header: "Created",
        id: "createdDate",
        enableSorting: true,
        enableColumnFilter: false,
        Cell: ({ row }) => renderCreatedDateColumn(row.original),
      }),
    );

    return cols;
  }, [
    activeTab,
    individuals,
    renderCategoryColumn,
    renderFromColumn,
    renderToColumn,
    renderDescriptionColumn,
    renderCreatedDateColumn,
  ]);

  const renderTopToolbarCustomActions = <T extends RowData>(
    props: { table: TableInstance<T> },
  ): JSX.Element => {
    const { table } = props;

    return (
      <TableTopToolbarContainer>
        <TableTitleContainer>
          <TableTitle variant="h4">Communications</TableTitle>
          {isMedDisplaySize && (
            <Tooltip title="New Message">
              <IconButton size="medium" color="primary" onClick={openComposeRoute}>
                <AddCommentIcon fontSize="large" />
              </IconButton>
            </Tooltip>
          )}
        </TableTitleContainer>
        <LowerTopToolbarContainer>
          <CommunicationsTabs
            value={activeTab}
            indicatorColor="primary"
            textColor="primary"
            variant={isMedDisplaySize ? "scrollable" : "standard"}
            scrollButtons={isMedDisplaySize ? "auto" : false}
            allowScrollButtonsMobile
            onChange={(_event, newValue) => navigate(`/communications/${newValue}`)}
          >
            <Tab value={BoxType.Inbox} label="Inbox" />
            <Tab value={BoxType.Outbox} label="Sent" />
            <Tab value={BoxType.Forums} label="Conversations" />
            <Tab value={BoxType.Archive} label="Archived" />
          </CommunicationsTabs>
          {activeTab === BoxType.Inbox && (
            <FilterButtons>
              <Chip
                icon={<FilterListIcon />}
                label="Unread"
                color="primary"
                variant={filterByUnread ? "filled" : "outlined"}
                onClick={() => {
                  setSearchParams((prevParams) => {
                    const params = new URLSearchParams(prevParams);
                    if (filterByUnread) {
                      params.delete("unread");
                    } else {
                      params.set("unread", "true");
                    }
                    return params;
                  });
                }}
              />
              <Chip
                icon={<FilterListIcon />}
                label="Action Required"
                color="primary"
                variant={filterByActionRequired ? "filled" : "outlined"}
                onClick={() => {
                  setSearchParams((prevParams) => {
                    const params = new URLSearchParams(prevParams);
                    if (filterByActionRequired) {
                      params.delete("actionRequired");
                    } else {
                      params.set("actionRequired", "true");
                    }
                    return params;
                  });
                }}
              />
            </FilterButtons>
          )}
        </LowerTopToolbarContainer>

        {isMedDisplaySize && (
          <MobileToolbarButtonsContainer>
            <GlobalFilterTextField table={table} />
            <ToolbarButtons>
              <ToggleFiltersButton table={table} />
              <ShowHideColumnsButton table={table} />
              <ToggleFullScreenButton table={table} />
            </ToolbarButtons>
          </MobileToolbarButtonsContainer>
        )}
      </TableTopToolbarContainer>
    );
  };

  const muiToolbarAlertBannerProps = useMemo(() => {
    if (errorLoadingQuery) {
      return { color: "error" as const, children: errorLoadingQuery };
    }

    return {
      action: (
        <AlertActions>
          {activeTab === BoxType.Archive && (
            <Button color="inherit" startIcon={<UnarchiveIcon />} onClick={unArchiveMultiple}>
              Un-Archive
            </Button>
          )}
          {activeTab === BoxType.Inbox && (
            <>
              {isMedDisplaySize ? (
                <Tooltip title="Mark as Read">
                  <span>
                    <IconButton color="inherit" onClick={markMultipleSeen}>
                      <MarkEmailReadIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              ) : (
                <Button color="inherit" startIcon={<MarkEmailReadIcon />} onClick={markMultipleSeen}>
                  Mark Read
                </Button>
              )}
              {isMedDisplaySize ? (
                <Tooltip title="Mark as Unread">
                  <span>
                    <IconButton color="inherit" onClick={markMultipleUnseen}>
                      <MarkEmailUnreadIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              ) : (
                <Button color="inherit" startIcon={<MarkEmailUnreadIcon />} onClick={markMultipleUnseen}>
                  Mark Unread
                </Button>
              )}
            </>
          )}
          {activeTab !== BoxType.Archive && (
            <>
              {isMedDisplaySize ? (
                <Tooltip title="Archive">
                  <span>
                    <IconButton color="inherit" onClick={archiveMultiple}>
                      <ArchiveIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              ) : (
                <Button color="inherit" startIcon={<ArchiveIcon />} onClick={archiveMultiple}>
                  Archive
                </Button>
              )}
            </>
          )}
        </AlertActions>
      ),
      sx: (theme: Theme) => ({
        "& .MuiBox-root .MuiStack-root": {
          alignItems: "baseline",
          gap: theme.spacing(1),
        },
      }),
    };
  }, [activeTab, archiveMultiple, errorLoadingQuery, isMedDisplaySize, markMultipleSeen, markMultipleUnseen, unArchiveMultiple]);

  const tableOptions: MRT_TableOptions<NotificationType | NotificationLoadIssue> = {
    columns,
    data: tableNotifications?.data ?? [],
    rowCount: tableNotifications?.headers.totalElements ?? 0,
    pageCount: Math.ceil((tableNotifications?.headers.totalElements ?? 0) / pagination.pageSize),
    enableRowSelection: activeTab !== BoxType.Forums,
    enableRowActions: activeTab !== BoxType.Forums && activeTab !== BoxType.Outbox,
    enableTableHead: true,
    displayColumnDefOptions: {
      "mrt-row-actions": {
        grow: 0,
        maxSize: actionsColumnSize,
        minSize: actionsColumnSize,
        size: actionsColumnSize,
        muiTableHeadCellProps: {
          sx: { px: 0.5 },
        },
        muiTableBodyCellProps: {
          align: "center",
          sx: {
            px: 0.5,
          },
        },
      },
    },
    muiSearchTextFieldProps: {
      placeholder: `Search ${activeTab}`,
      slotProps: {
        input: {
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
          endAdornment: globalFilter ? (
            <InputAdornment position="end">
              <IconButton
                aria-label="Clear search"
                size="small"
                onClick={() => setGlobalFilter("")}
              >
                <CloseIcon />
              </IconButton>
            </InputAdornment>
          ) : undefined,
        },
      },
    },
    muiTableBodyRowProps: ({ row }) =>
      notificationRowProps(row.original, {
        isArchived: activeTab === BoxType.Archive,
        userId: session.user?.id,
        onSelect: () => {
          void navigateToTarget(row.original);
        },
      }),
    muiTableProps: () => ({
      sx: {
        height: tableNotifications?.data ? "initial" : "100%",
      },
    }),
    renderToolbarInternalActions: ({ table }) => (
      <>
        {!isMedDisplaySize && (
          <ToolbarButtonsContainer>
            <ToolbarButtons>
              <GlobalFilterTextField table={table} sx={searchFieldSpacing} />
              <ToggleFiltersButton table={table} />
              <ShowHideColumnsButton table={table} />
              <ToggleFullScreenButton table={table} />
            </ToolbarButtons>
            <ToolbarActionButtons>
              <Button
                color="primary"
                size="large"
                startIcon={<AddCommentIcon fontSize="large" />}
                onClick={openComposeRoute}
              >
                New Message
              </Button>
            </ToolbarActionButtons>
          </ToolbarButtonsContainer>
        )}
      </>
    ),
    renderTopToolbarCustomActions,
    renderRowActions: ({ row }) => {
      if (activeTab === BoxType.Archive) {
        return renderArchiveRowActions(row.original);
      }
      if (activeTab === BoxType.Inbox) {
        return renderInboxRowActions(row.original);
      }
    },
    renderEmptyRowsFallback: emptyRowsFallback({
      // The unread and action-required chips are the page's own, and an inbox narrowed to nothing by
      // one of them is not an empty inbox.
      hasPageFilters: Boolean(filterByUnread) || Boolean(filterByActionRequired),
      icon: <AllCaughtUp alt="" src={EmptyInboxPlaceholder} />,
      isError: Boolean(errorLoadingQuery),
      isLoading: queryIsLoading,
      onRetry: () => void refreshNotifications(),
      // The tab names the box; the message has to name what is in it. "No matching inbox" is not a
      // sentence about anything.
      subject: activeTab === BoxType.Archive ? "archived communications" : "communications",
    }),
    getRowId: (row) => row.id?.value ?? Guid.generate().value,
    initialState: {
      columnPinning: { right: ["mrt-row-actions"] },
      columnOrder: [
        "type",
        "from",
        "to",
        "description",
        "message",
        "conversationName",
        "createdDate",
        "actions",
      ],
      showGlobalFilter: true,
    },
    manualFiltering: true,
    enableColumnFilters: true,
    enableStickyHeader: true,
    enableStickyFooter: true,
    enableColumnPinning: true,
    manualPagination: true,
    manualSorting: true,
    layoutMode: "grid",
    muiTableContainerProps: {
      sx: {
        flex: 1,
        minWidth: 0,
        position: "relative"
      },
    },
    muiTablePaperProps: {
      sx: {
        height: "100%",
        display: "flex",
        flexDirection: "column",
        maxWidth: "100%",
        minWidth: 0,
        overflow: "hidden"
      },
    },
    muiTopToolbarProps: {
      sx: {
        maxWidth: "100%",
        minWidth: 0,
        overflow: "hidden",
        "& > .MuiBox-root": {
          gap: 0,
          minWidth: 0,
          overflow: "hidden",
        },
      },
    },
    muiTableBodyProps: {
      sx: {
        height: "100%",
        "& tr:only-of-type td[colspan]": {
          height: "100%",
          verticalAlign: "middle",
          padding: 0,
          borderBottom: "none",
        },
      },
    },
    muiToolbarAlertBannerProps: muiToolbarAlertBannerProps,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    onRowSelectionChange: setRowSelection,
    positionGlobalFilter: "none",
    state: {
      columnFilters,
      globalFilter,
      isLoading: queryIsLoading,
      pagination,
      density: isXlDisplaySize ? "compact" : "comfortable",
      showAlertBanner: Boolean(errorLoadingQuery),
      showProgressBars: queryIsLoading,
      sorting,
      rowSelection,
      showSkeletons: queryIsLoading && !tableNotifications,
    },
  };

  if (!sessionPermissions) return permissionsLoadState;
  if (!sessionPermissions.includesAccessToCommunications) return <NotAuthorized />;

  if (activeTab === BoxType.Forums) {
    return <ConversationTable renderTopToolbarCustomActions={renderTopToolbarCustomActions} />;
  } else return <MRTNoMemo<NotificationType | NotificationLoadIssue> options={tableOptions} />;
}

function getIndividualFilterOptionsFromSearchResults(results: MarketplaceSearchInfoJSON[]): IndividualFilterOption[] {
  return dedupeIndividualFilterOptions(
    results.flatMap((result) => {
      const option = getIndividualFilterOptionFromSearchResult(result);
      return option ? [option] : [];
    }),
  );
}

function getIndividualFilterOptionFromSearchResult(
  result: MarketplaceSearchInfoJSON,
): IndividualFilterOption | undefined {
  if (!result.userId) return undefined;

  const fullName = [result.firstName, result.lastName].filter(Boolean).join(" ").trim();

  return {
    label: fullName || result.email || result.userId,
    secondaryLabel: result.companyName ?? undefined,
    value: result.userId,
  };
}

function getIndividualFilterOptionFromIndividual(individual: Individual): IndividualFilterOption {
  return {
    label: individual.fullName,
    secondaryLabel: individual.entityName,
    value: individual.userId.value,
  };
}

function dedupeIndividualFilterOptions(options: IndividualFilterOption[]): IndividualFilterOption[] {
  return [...new Map(options.map((option) => [option.value, option])).values()];
}

function getSelectedNotificationFilterUserIds(columnFilters: ColumnFiltersState): string[] {
  return columnFilters
    .filter((filter) => (filter.id === "from" || filter.id === "to") && typeof filter.value === "string")
    .map((filter) => filter.value as string)
    .filter((value) => Guid.isValid(value));
}

function getValidNotificationUserIds(
  tableNotifications: PaginatedResponse<NotificationLoadIssue | NotificationType> | undefined,
): Set<string> {
  const validNotifications: NotificationType[] = [];
  if (!tableNotifications) return new Set();

  tableNotifications.data.forEach((notification) => {
    if (!isNotificationLoadIssue(notification)) {
      validNotifications.push(notification);
    }
  });

  const uniqueSenderIds = new Set(
    validNotifications
      .map((notification: NotificationType) => notification.senderUserId?.value)
      .filter((senderId) => senderId !== undefined),
  );
  const uniqueRecipientIds = tableNotifications
    ? new Set(
      validNotifications.flatMap((notification: NotificationType) =>
        notification.recipients.map((recipient) => recipient.userId.value).filter((userId) => userId !== undefined),
      ),
    )
    : new Set<string>();

  return new Set([...uniqueSenderIds, ...uniqueRecipientIds]);
}

function isBoxType(value: unknown): value is BoxType {
  return Object.values(BoxType).includes(value as BoxType);
}

function getStoredCommunicationsTab(): BoxType | undefined {
  try {
    const storedTab = globalThis.sessionStorage?.getItem("communications.activeTab");
    return isBoxType(storedTab) ? storedTab : undefined;
  } catch {
    return undefined;
  }
}

function storeCommunicationsTab(tab: BoxType): void {
  try {
    globalThis.sessionStorage?.setItem("communications.activeTab", tab);
  } catch {
    // Ignore storage failures; routing still provides a default tab.
  }
}

/**
 * The label the Type column shows, and the value it filters and sorts on.
 *
 * Exported so it can be covered directly rather than through a rendered page — see the note at the
 * top of `communications-page.spec.tsx`.
 */
export function getNotificationTypeLabel(notification: NotificationType | NotificationLoadIssue): string {
  if (isNotificationLoadIssue(notification)) return "Unknown";

  const topicCategory = notification.topic?.category;
  if (!topicCategory) return "Notification";
  // A new-message notification is topic-associated with the forum the message was posted in
  // (#3144), so it arrives under the same `Messaging.Forum` category a forum-level notification
  // would. The label still names the message it announces rather than the forum.
  if (topicCategory === TopicCategory.Messaging.Forum) {
    // A run of messages in one forum is folded into the notification already sitting unread in the
    // recipient's inbox rather than sent one row per message (#830), so one row can stand for
    // several. The label says how many. The Type filter matches on substrings, so a reader filtering
    // for "New Message" still finds the aggregated rows.
    const messageCount = notification.occurrenceCount;
    return messageCount > 1 ? `${messageCount} New Messages` : "New Message";
  }

  return Config.notificationTypes[topicCategory as keyof typeof Config.notificationTypes] ?? "Notification";
}

/**
 * The tag the Description column shows for a notification that stands for more than one event
 * (#830), or `null` for the ordinary single-event notification that needs no tag.
 *
 * The description itself is the *latest* message of an aggregated run, so without this the row reads
 * as a single message and the earlier ones are invisible. Only categories the API aggregates can
 * reach a count above one, so nothing here has to name them.
 *
 * Exported so it can be covered directly rather than through a rendered page — see the note at the
 * top of `communications-page.spec.tsx`.
 */
export function getNotificationOccurrenceLabel(
  notification: NotificationType | NotificationLoadIssue,
): string | null {
  if (isNotificationLoadIssue(notification)) return null;

  const occurrenceCount = notification.occurrenceCount;
  if (!occurrenceCount || occurrenceCount < 2) return null;

  return `${occurrenceCount} messages`;
}

function getNotificationSenderFilterValue(notification: NotificationType | NotificationLoadIssue): string {
  if (isNotificationLoadIssue(notification)) return "";
  if ("messageInfo" in notification && notification.messageInfo?.senderName?.value) {
    return notification.messageInfo.senderName.value;
  }

  return notification.senderName?.value ?? "System";
}

function getNotificationRecipientFilterValue(notification: NotificationType | NotificationLoadIssue): string {
  if (isNotificationLoadIssue(notification)) return "";
  return notification.recipientName?.value ?? notification.recipients?.map((recipient) => recipient.userId.value).join(", ") ?? "";
}

function getNotificationDescriptionFilterValue(notification: NotificationType | NotificationLoadIssue): string {
  if (isNotificationLoadIssue(notification)) return "";
  return notification.message?.value ?? "";
}

function getNotificationCreatedDateSortValue(notification: NotificationType | NotificationLoadIssue): number {
  if (isNotificationLoadIssue(notification)) return 0;
  return notification.createdDate?.value ?? 0;
}
